<ng-template
  let-extraClass="extraClass"
  #topNav>
  @if (!windowService?.isDesktopRes) {
    <tidy-top-nav
      [class]="extraClass + (!isLoaded() ? ' suppress-loading-bar' : '')"
      [title]="'Overview'"
      [canGoBack]="false" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav; context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper [isPrimaryLayer]="true" [hasBottomNav]="!windowService?.isDesktopRes" class="strategy-page">
    <ng-container *ngTemplateOutlet="topNav; context:{ extraClass: 'desktop' }" />

    @if (windowService?.isDesktopRes) {
      <tidy-desktop-header>
        <div class="wrappable-container">
          <tidy-text [title]="'Property P&L'" />
          <tidy-header-select
            [items]="propertyItems()"
            [icon]="'assets/svg/home-outline.svg'"
            [selectedValue]="propertyValue()"
            (optionChange)="onPropertyChange($event)" />
          <tidy-header-select
            [items]="viewItems"
            [icon]="'assets/svg/calendar-outline.svg'"
            [selectedValue]="view()"
            (optionChange)="onViewChange($event)" />
          <tidy-month-attribution-switch
            [value]="attribution()"
            (valueChange)="onAttributionChange($event)" />
        </div>
        <div class="non-wrappable-right-aligned-container">
          @if (showRefreshSpinner) {
            <ion-spinner name="lines" class="header-loading-spinner" style="margin-right: 15px" />
          }
          <automations-preview-button
            [tag]="'overview'" />
        </div>
      </tidy-desktop-header>
    }

    <!-- The skeletons float in on landing (sk-section entrance animation);
         when data lands the real content replaces every skeleton in place
         instantly, with no animation. The placeholders share the real
         wrappers/classes so the swap happens without the page shifting. -->
    <div class="strategy-body" [class.strategy-scrollable-content]="windowService?.isDesktopRes">
    @if (!windowService?.isDesktopRes) {
      <!-- Mobile filter row — same `tidy-header-select` pills as the desktop
           header above, sharing the same bindings (propertyItems / view). -->
      @if (isLoaded()) {
        <div class="mobile-filter-row">
          <tidy-header-select
            [items]="propertyItems()"
            [icon]="'assets/svg/home-outline.svg'"
            [selectedValue]="propertyValue()"
            (optionChange)="onPropertyChange($event)" />
          <tidy-header-select
            [items]="viewItems"
            [icon]="'assets/svg/calendar-outline.svg'"
            [selectedValue]="view()"
            (optionChange)="onViewChange($event)" />
          <tidy-month-attribution-switch
            [value]="attribution()"
            (valueChange)="onAttributionChange($event)" />
        </div>
      } @else {
        <div class="mobile-filter-row sk-section">
          <ion-skeleton-text [animated]="true" class="sk-filter-pill" />
          <ion-skeleton-text [animated]="true" class="sk-filter-pill" />
        </div>
      }
    }

    <!-- KPI tiles -->
    @if (isLoaded()) {
      <div class="kpi-row">
        <tidy-kpi-tile
          [label]="'Gross Revenue'"
          [value]="formatCurrency(revenueActual())"
          [icon]="'assets/svg/cash-outline.svg'"
          [colorClass]="'icon-revenue'" />
        <tidy-kpi-tile
          [label]="'Operating Expenses'"
          [value]="formatCurrency(expensesActual())"
          [icon]="'assets/svg/calculator-outline.svg'"
          [colorClass]="'icon-expense'" />
        <tidy-kpi-tile
          [label]="'Net Operating Income'"
          [value]="formatCurrency(netActual())"
          [icon]="'assets/svg/trending-up-outline.svg'"
          [colorClass]="'icon-noi'" />
        @if (occupancyRate() !== null) {
          <tidy-kpi-tile
            [label]="'Occupancy'"
            [value]="occupancyRate() + '%'"
            [icon]="'assets/svg/calendar-outline.svg'"
            [colorClass]="'icon-occupancy'" />
        }
        @if (avgNightlyRate() !== null) {
          <tidy-kpi-tile
            [label]="'Avg Nightly Rate'"
            [value]="'$' + avgNightlyRate()"
            [icon]="'assets/svg/key-outline.svg'"
            [colorClass]="'icon-adr'" />
        }
        @if (avgStarRatingDisplay() !== null) {
          <tidy-kpi-tile
            [label]="'Avg Star Rating'"
            [value]="avgStarRatingDisplay()!"
            [icon]="'assets/svg/star-outline.svg'"
            [colorClass]="'icon-rating'" />
        }
        @if (turnoverCompletionDisplay() !== null) {
          <tidy-kpi-tile
            [label]="'Turnover Completion'"
            [value]="turnoverCompletionDisplay()!"
            [icon]="'assets/svg/checkmark-done-outline.svg'"
            [colorClass]="'icon-turnover'" />
        }
        @if (guestResponseDisplay() !== null) {
          <tidy-kpi-tile
            [label]="'Guest Response Time'"
            [value]="guestResponseDisplay()!"
            [icon]="'assets/svg/chatbubbles-outline.svg'"
            [colorClass]="'icon-guest-response'"
            [caption]="'All Properties' | translate" />
        }
        @if (proResponseDisplay() !== null) {
          <tidy-kpi-tile
            [label]="'Pro Response Time'"
            [value]="proResponseDisplay()!"
            [icon]="'assets/svg/people-outline.svg'"
            [colorClass]="'icon-pro-response'"
            [caption]="'All Properties' | translate" />
        }
        @if (inquiryResponseDisplay() !== null) {
          <tidy-kpi-tile
            [label]="'Inquiry Response Time'"
            [value]="inquiryResponseDisplay()!"
            [icon]="'assets/svg/mail-outline.svg'"
            [colorClass]="'icon-inquiry-response'" />
        }
      </div>
    } @else {
      <div class="kpi-row">
        @for (i of [1, 2, 3, 4, 5]; track i) {
          <div class="kpi-card sk-section">
            <ion-skeleton-text [animated]="true" class="sk-kpi-icon" />
            <div class="kpi-body">
              <ion-skeleton-text [animated]="true" class="sk-kpi-label" />
              <ion-skeleton-text [animated]="true" class="sk-kpi-value" />
            </div>
          </div>
        }
      </div>
    }

    <!-- Chart + Suggestions side-by-side -->
    <div class="main-grid">
      @if (isLoaded()) {
        <section class="card chart-card">
          <header class="card-header">
            <tidy-text [body]="'P&L Trends'" class="card-title bold" />
            <div class="chart-legend">
              <span class="legend-item">
                <span class="legend-swatch swatch-revenue-actual"></span>
                <tidy-text [helper]="'Actual Revenue'" class="legend-label" />
              </span>
              <span class="legend-item">
                <span class="legend-swatch swatch-revenue-projected"></span>
                <tidy-text [helper]="'Projected Revenue'" class="legend-label" />
              </span>
              <span class="legend-item">
                <span class="legend-swatch swatch-net-actual"></span>
                <tidy-text [helper]="'Net Income (Actual)'" class="legend-label" />
              </span>
              <span class="legend-item">
                <span class="legend-swatch swatch-net-projected"></span>
                <tidy-text [helper]="'Net Income (Projected)'" class="legend-label" />
              </span>
            </div>
          </header>
          <tidy-lazy-chart
            [series]="chartConfig().series"
            [chart]="chartConfig().chart"
            [stroke]="chartConfig().stroke"
            [xaxis]="chartConfig().xaxis"
            [yaxis]="chartConfig().yaxis"
            [grid]="chartConfig().grid"
            [tooltip]="chartConfig().tooltip"
            [colors]="chartConfig().colors"
            [plotOptions]="chartConfig().plotOptions"
            [fill]="chartConfig().fill"
            [markers]="chartConfig().markers"
            [legend]="chartConfig().legend"
            class="trend-chart" />
        </section>

        <aside class="card suggestions-card">
          <header class="card-header">
            <tidy-text [body]="'Suggestions to Improve'" class="card-title bold" />
          </header>
          @if (hasSuggestions()) {
            <ul class="suggestions-list">
              @for (suggestion of conciergeSuggestions(); track suggestion.id) {
                <li class="suggestion concierge-suggestion">
                  <tidy-image [src]="'assets/svg/bulb-outline.svg'" class="suggestion-icon" />
                  <div class="suggestion-content">
                    @if (suggestion.title) {
                      <div class="suggestion-title">{{ suggestion.title }}</div>
                    }
                    @if (!suggestion.address_id) {
                      <div class="suggestion-scope">{{ 'Account-wide' | translate }}</div>
                    }
                    @if (suggestion.description) {
                      <div class="suggestion-body">{{ suggestion.description }}</div>
                    }
                    @if (canActOnSuggestions) {
                      <div class="suggestion-actions">
                        <button
                          type="button"
                          class="suggestion-action approve"
                          [disabled]="processingSuggestionId() === suggestion.id"
                          (click)="approveSuggestion(suggestion)">
                          {{ (processingSuggestionId() === suggestion.id ? 'Approving…' : 'Approve') | translate }}
                        </button>
                        <button
                          type="button"
                          class="suggestion-action reject"
                          [disabled]="processingSuggestionId() === suggestion.id"
                          (click)="rejectSuggestion(suggestion)">
                          {{ 'Reject' | translate }}
                        </button>
                      </div>
                    } @else {
                      <div class="suggestion-note">
                        {{ 'Only account owners and admins can approve or reject suggestions.' | translate }}
                      </div>
                    }
                  </div>
                </li>
              }
              @for (suggestion of effectiveSuggestions(); track suggestion.title) {
                <li class="suggestion">
                  <tidy-image [src]="suggestion.icon" class="suggestion-icon" />
                  <div class="suggestion-content">
                    <div class="suggestion-title">{{ suggestion.title }}</div>
                    <div class="suggestion-body">{{ suggestion.body }}</div>
                    <div class="suggestion-meta">
                      <span
                        class="impact-pill"
                        [class.positive]="suggestion.impact === 'positive'"
                        [class.negative]="suggestion.impact === 'negative'">{{ suggestion.metric }}</span>
                    </div>
                  </div>
                </li>
              }
            </ul>
          } @else {
            <div class="suggestions-empty">
              <tidy-image [src]="'assets/svg/bulb-outline.svg'" class="suggestions-empty__icon" />
              <tidy-text [helper]="'Suggestions will be generated after 30 days of activity.'" class="suggestions-empty__body" />
            </div>
          }
        </aside>
      } @else {
        <section class="card chart-card sk-section">
          <header class="card-header">
            <ion-skeleton-text [animated]="true" class="sk-card-title" />
            <div class="chart-legend">
              @for (i of [1, 2, 3, 4]; track i) {
                <span class="legend-item">
                  <ion-skeleton-text [animated]="true" class="sk-legend-swatch" />
                  <ion-skeleton-text [animated]="true" class="sk-legend-label" />
                </span>
              }
            </div>
          </header>
          <div class="sk-chart">
            <div class="sk-chart-body">
              <div class="sk-chart-yaxis">
                @for (i of [1, 2, 3, 4, 5]; track i) {
                  <ion-skeleton-text [animated]="true" class="sk-yaxis-label" />
                }
              </div>
              <div class="sk-chart-plot">
                @for (i of [1, 2, 3, 4, 5, 6]; track i) {
                  <div class="sk-chart-group">
                    <ion-skeleton-text [animated]="true" class="sk-bar sk-bar-actual" />
                    <ion-skeleton-text [animated]="true" class="sk-bar sk-bar-projected" />
                  </div>
                }
              </div>
              <div class="sk-chart-yaxis right">
                @for (i of [1, 2, 3, 4, 5]; track i) {
                  <ion-skeleton-text [animated]="true" class="sk-yaxis-label" />
                }
              </div>
            </div>
            <div class="sk-chart-xaxis">
              @for (i of [1, 2, 3, 4, 5, 6]; track i) {
                <span class="sk-xaxis-slot">
                  <ion-skeleton-text [animated]="true" class="sk-xaxis-label" />
                </span>
              }
            </div>
          </div>
        </section>

        <aside class="card suggestions-card sk-section">
          <header class="card-header">
            <ion-skeleton-text [animated]="true" class="sk-card-title" />
          </header>
          <ul class="suggestions-list">
            @for (i of [1, 2, 3, 4, 5]; track i) {
              <li class="suggestion sk-suggestion">
                <ion-skeleton-text [animated]="true" class="sk-suggestion-icon" />
                <div class="suggestion-content">
                  <ion-skeleton-text [animated]="true" class="sk-suggestion-title" />
                  <ion-skeleton-text [animated]="true" class="sk-suggestion-line" />
                  <ion-skeleton-text [animated]="true" class="sk-suggestion-line short" />
                  <ion-skeleton-text [animated]="true" class="sk-suggestion-pill" />
                </div>
              </li>
            }
          </ul>
        </aside>
      }
    </div>

    <!-- P&L Summary table -->
    @if (isLoaded()) {
      <section class="card summary-card">
        <header class="card-header">
          <tidy-text [body]="('P&L Summary (' | translate) + currentViewLabel() + ')'" class="card-title bold" />
        </header>
        <div class="summary-table-scroll">
        <table class="summary-table">
          <thead>
            <tr>
              <th class="col-label"></th>
              <th class="col-num">{{ actualColumnLabel() }}</th>
              <th class="col-num">Projected</th>
              <th class="col-num">Variance ($)</th>
              <th class="col-num">Variance (%)</th>
            </tr>
          </thead>
          <tbody>
            @for (row of summaryDetailRows(); track row.subcategory) {
              <tr class="data-row">
                <td class="col-label">
                  <span>{{ row.label }}</span>
                </td>
                <td class="col-num">{{ formatCurrency(rowDisplayAmount(row.actual)) }}</td>
                <td class="col-num">{{ formatCurrency(rowDisplayAmount(row.projected)) }}</td>
                <td
                  class="col-num"
                  [class.positive]="varianceClass(row) === 'positive'"
                  [class.negative]="varianceClass(row) === 'negative'">
                  {{ formatSignedCompact(rowDisplayAmount(row.variance)) }}
                </td>
                <td
                  class="col-num"
                  [class.positive]="varianceClass(row) === 'positive'"
                  [class.negative]="varianceClass(row) === 'negative'">
                  {{ formatVariancePct(row.variance_percent) }}
                </td>
              </tr>
            }

            @if (netIncomeRow(); as net) {
              <tr class="net-row">
                <td class="col-label">{{ net.label }}</td>
                <td class="col-num">{{ formatCurrency(rowDisplayAmount(net.actual)) }}</td>
                <td class="col-num">{{ formatCurrency(rowDisplayAmount(net.projected)) }}</td>
                <td
                  class="col-num"
                  [class.positive]="varianceClass(net) === 'positive'"
                  [class.negative]="varianceClass(net) === 'negative'">
                  {{ formatSignedCompact(rowDisplayAmount(net.variance)) }}
                </td>
                <td
                  class="col-num"
                  [class.positive]="varianceClass(net) === 'positive'"
                  [class.negative]="varianceClass(net) === 'negative'">
                  {{ formatVariancePct(net.variance_percent) }}
                </td>
              </tr>
            }
          </tbody>
        </table>
        </div>
      </section>
    } @else {
      <section class="card summary-card sk-section">
        <header class="card-header">
          <ion-skeleton-text [animated]="true" class="sk-card-title wide" />
        </header>
        <div class="summary-table-scroll">
        <table class="summary-table sk-summary-table">
          <thead>
            <tr>
              <th class="col-label"></th>
              @for (i of [1, 2, 3, 4]; track i) {
                <th class="col-num"><ion-skeleton-text [animated]="true" class="sk-th" /></th>
              }
            </tr>
          </thead>
          <tbody>
            @for (i of [1, 2, 3, 4, 5, 6]; track i) {
              <tr class="data-row">
                <td class="col-label"><ion-skeleton-text [animated]="true" class="sk-cell-label" /></td>
                @for (j of [1, 2, 3, 4]; track j) {
                  <td class="col-num"><ion-skeleton-text [animated]="true" class="sk-cell-num" /></td>
                }
              </tr>
            }
            <tr class="net-row">
              <td class="col-label"><ion-skeleton-text [animated]="true" class="sk-cell-label sk-cell-net" /></td>
              @for (j of [1, 2, 3, 4]; track j) {
                <td class="col-num"><ion-skeleton-text [animated]="true" class="sk-cell-num sk-cell-net" /></td>
              }
            </tr>
          </tbody>
        </table>
        </div>
      </section>
    }
    </div>
  </tidy-wrapper>
</ng-template>

<app-scroll-wrapper [isfullWidthContent]="true" [templateContent]="content" />

<tidy-bottom-nav activeRoute="/overview" />

@if (showAboutThisPage()) {
  <tidy-about-this-page [page]="'strategy'" (dismissed)="dismissAboutThisPage()" />
}
